"use client";

/**
 * DealCard — single-card render in the category-page deal grid + the
 * homepage "Nieuw toegevoegd" carousel.
 *
 * Renders:
 *   - hero image with discount badge
 *   - bed/plus/ticket icon row + voucher badge (if voucher deal)
 *   - localized title (resolved via getLocalizedField — ADR 0004)
 *   - price block: "van" (strikethrough, sourced from backend
 *     `price_history_gate.pMin30Eur` for DE),
 *     "nu vanaf" (now-price), and a market-specific discount claim.
 *     Ordinary deals never derive a claim from legacy CMS price fields. The
 *     NL roundtrip portfolio has one explicit 15-30% list-price contract,
 *     reconciled against `lowest_price_calculated` before rendering.
 *   - homepage variant suppresses the price block (Kartik #4)
 *
 * Props:
 *   - deal            (Object, required) — deal payload from the API.
 *                     Required shape: `deals_id`, `title` (string OR
 *                     multilang-JSON), `deals_img`, optional
 *                     `price_history_gate` for verified claims,
 *                     plus EITHER `lowest_price_before_discount` (deals)
 *                     OR `value_cents` / `selling_price` (vouchers).
 *   - categoryName    (string) — category slug for the URL builder
 *   - filterParams    (Object) — { adults, children, nights, date }
 *                     forwarded to the booking flow
 *   - showPricing     (boolean, default true) — set false to hide the
 *                     price block (homepage carousel)
 *   - isHomepage      (boolean, default false) — variant tag for layout
 *   - titleOverride   (string, optional) — 2026-07-18 (thema-campagne-
 *                     pagina's): when set, renders this text INSTEAD of
 *                     the resolved `getLocalizedField(deal, "title", ...)`
 *                     value, for both the heading and the image alt text.
 *                     Used for a theme relation's `customHeadline`
 *                     override, scoped to that ONE landing page. Does
 *                     NOT touch `deal.title`/`deal.i18n` — the outbound
 *                     `href` (dealUrl/voucherUrl) still resolves from the
 *                     deal's REAL title, so the campaign headline can
 *                     never corrupt the canonical detail-page link slug.
 *   - hrefOverride    (string, optional): 2026-09-29 (Luxe-ledenpoort
 *                     LP8): when set, the card links HERE instead of the
 *                     dealUrl/voucherUrl result, without filterParams. The
 *                     /luxe hub uses it so a Luxe card opens /luxe/<id>
 *                     (the member-gated route) instead of the public
 *                     /packages/... detail door. Unset = unchanged behaviour.
 *
 *   - opmaak          ("standaard" | "raster", default "standaard"): "raster"
 *                     is de kaart voor het homepageraster (beeld 4:3, geen
 *                     kaartrand, prijsblok onderaan). Alleen klassen, maten en
 *                     volgorde; bedragen, teksten, links en tracking zijn gelijk.
 *
 * Multilang note: `deal.title` MAY be a multilang JSON wrapper
 * (Kartik #87 / #88 / #89). The component resolves once via
 * `getLocalizedField` and reuses for both alt-text and heading.
 * Slugify also unwraps before producing the URL slug — see ADR 0004.
 */
import Link from "next/link";
import Image from "next/image";
import { imgUrl } from "@/lib/api";
import {
  formatPrice,
  formatPriceText,
  formatStrikethroughPrice,
  computePackageTotal,
  RESERVATION_FEE_EUR,
} from "@/lib/format-price";
import { resolvePriceClaimDisplay } from "@/lib/price-math";
import { resolveReservationFeeEur, isAllInclusiveReservationFee } from "@/lib/reservation-fee";
import { convertEurTo } from "@/lib/currency-convert";
import { dealUrl, voucherUrl } from "@/lib/slugify";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { deTekst } from "@/lib/de-text";
import { getLocalizedField } from "@/lib/localized-field";
import { fireClickBeacon } from "@/components/personalization/RecordDealClick";
import { CARD_IMAGE_QUALITY } from "@/lib/image-quality";
import RecordedBadge from "@/components/personalization/RecordedBadge";
import { resolveDisplayPrice } from "@/lib/resolve-display-price";
import { isLuxeDeal } from "@/lib/luxe/luxe-deal";
import KaartFilm from "@/components/home/KaartFilm";
import { useSyncExternalStore } from "react";
import { abonneerFilmStand, bewegingIsUit, dealFilmUit, dealFilmUitgesloten, useDealFilms } from "@/lib/dealfilm";
// Defensive hardening, 2026-08-15 (found while investigating a reported
// reserveringskosten-hint leak on deal 229's "Vergelijkbare arrangementen"
// strip — that specific report turned out to be a misreading: the two
// hints belonged to regular deals 206/198 rendering their own correct
// total-price hint, and 229's own related-strip was already clean. NOT a
// confirmed live bug). This component's own local `is_voucher_deal ===
// true || === "true"` check is still a second, drifted hand-copy of the
// exact class of check the shared helper's doc comment warns about — it
// doesn't cover the numeric/`"1"` affirmative forms is-voucher-deal.js
// documents and is-voucher-deal.test.js pins as valid inputs, even though
// no live door (package/allPackage/voucher-as-catalogue) has been
// verified to actually emit them today. Switched to the canonical helper
// as drift-prevention: every DealCard instance (related, alternative,
// category grid, homepage carousel, search) now agrees with the hero
// card and the deal-detail page's own gate on every input shape the
// helper accepts, not just the ones this local copy happened to expect.
import { isVoucherDeal } from "@/lib/is-voucher-deal";
import { dealHasActivity } from "@/lib/booking/voucher-stay-cost";
// Dennis 28-09-2026: "Keuze uit N hotels" weghalen (nooit gevraagd, kwam
// uit audit #56/fe#1195). Niet terugzetten zonder zijn opdracht. Removed
// the hotelChoiceCount/isRoundtripCatalogDeal imports that only fed that
// line — hotel-count.js itself stays (still used elsewhere).
import { readCustomerPriceClaim } from "@/lib/strikethrough-gate";
import { categoryStickerFor } from "@/lib/deal-category-sticker";
import { useFixedDateVoucherUnavailable, useVoucherSaleClosed } from "@/lib/use-fixed-date-voucher-unavailable";

export default function DealCard({
  deal,
  categoryName,
  filterParams,
  showPricing = true,
  isHomepage = false,
  source,
  position,
  imageLoading,
  imageSizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw",
  showRecordedBadge = false,
  titleOverride,
  hrefOverride,
  beeldExtra,
  opmaak = "standaard",
}) {
  const fixedDateVoucherUnavailable = useFixedDateVoucherUnavailable(deal);
  // Fixronde 3 #1920: the same net for a voucher past its last sales day (182).
  const voucherSaleClosed = useVoucherSaleClosed(deal);
  const { currency, effectiveRate, locale, key: marketKey, farTravelBookingEnabled } = useSiteContext();
  const { t } = getTranslations(locale);
  // Dealvideo uit het register (GET /deal-video): de kaartfilm ligt over de foto.
  // Niet voor Exclusive-kaarten (Luxe-categorie of hrefOverride): die lezen het
  // register niet en houden precies hun beeld. Server-render = lege stand.
  // ING en extern verkochte deals krijgen nooit een film (dealFilmUitgesloten).
  const dealFilmsToegestaan = !hrefOverride && !isLuxeDeal(deal) && !dealFilmUitgesloten(deal, { locale });
  const dealFilms = useDealFilms({ actief: dealFilmsToegestaan });
  // Server en eerste render: aan (geen hydratieverschil); de client corrigeert daarna.
  const bewegingUit = useSyncExternalStore(abonneerFilmStand, bewegingIsUit, () => false);
  // Global visible-card safety net for homepage, themed and related grids.
  // Server loaders filter too, but this also closes an already-open tab at
  // the Amsterdam cutoff and protects any future DealCard caller by default.
  if (fixedDateVoucherUnavailable || voucherSaleClosed) return null;
  // Kartik #89 (2026-04-25): some deal titles come back from the API
  // as a multilang JSON blob (e.g. `{"en":"Disneyland …","nl":"Disneyland …"}`).
  // Resolve once, reuse for alt-text + heading so the card never leaks
  // the raw `{` wrapper into the DOM.
  // titleOverride (2026-07-18, thema-campagnepagina's) short-circuits this
  // resolution for display only — see the component doc-comment above.
  // Fase 2 (2026-07-24): market-aware — a Belgian card (marketKey "nl-be"/
  // "fr-be") shows the Belgian-written title from deal.i18n when present, so
  // the card matches the deal-detail H1. No-op on .nl ("nl-nl" has no slot).
  const displayTitle =
    titleOverride ||
    getLocalizedField(deal, "title", locale, { market: marketKey }) ||
    deal.title ||
    "Reisdeal";
  // WP4: prices in the API are EUR. Convert to the visitor's currency
  // (EUR passthrough, GBP multiplies by effectiveRate) before formatting.
  const toCurrency = (eur) => convertEurTo(eur, currency, effectiveRate);
  // imgUrl() accepts absolute URLs (e.g. a RateHawk CDN photo) as-is and only
  // prepends IMG_BASE for relative CMS paths. Centralised in @/lib/api so every
  // card/hero/cross-sell surface resolves images the same way; blindly
  // prepending IMG_BASE broke deals whose image is an external URL (deal 292).
  const imgSrc = imgUrl(deal.deals_img) || "/images/placeholder-deal.svg";

  // C21 fix (Fable5 fix-campagne, HIGH-tier): route through the
  // canonical resolveDisplayPrice instead of a hand-copied chain, so
  // this surface can never drift from DealCard's sibling surfaces
  // (voucher detail hero, listing/category cards, cross-sell,
  // search dropdown, JSON-LD). resolveDisplayPrice implements the
  // exact same fixed_display_price → (voucher | non-voucher) chain
  // this file used to hand-copy — see its docblock for the full
  // priority order.
  const actualPrice = resolveDisplayPrice(deal);

  // Stap 6 of Dennis's "for once and for all" plan (2026-05-04): if a
  // deal-list endpoint somehow leaks a zero-priced deal past the
  // backend WHERE_RENDERABLE filter (data race, new endpoint that
  // skipped the helper, stale Cloudflare cache serving a pre-filter
  // payload), the card renders nothing rather than a misleading "€ 0,-"
  // placeholder card. The customer sees one fewer card on the page;
  // they do NOT see a promise of a free trip that doesn't exist.
  //
  // Defensive layer — backend filter (PR #421 + WHERE_RENDERABLE) is
  // still the primary defense. This is a render-time fail-safe.
  if (actualPrice <= 0) {
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
       
      console.warn(
        `[DealCard] Refusing to render zero-priced deal id=${deal?.id ?? deal?.deals_id} title=${displayTitle.slice(0, 60)}. ` +
          `Backend filter likely missed this entry — investigate /deals/most-viewed or whichever feed surfaced it.`,
      );
    }
    return null;
  }
  const isVoucher = isVoucherDeal(deal);
  const priceHistoryClaim = readCustomerPriceClaim(deal, marketKey, {
    currentPriceEur: actualPrice,
  });
  const discount =
    priceHistoryClaim?.displayRule === "badge" || priceHistoryClaim?.displayRule === "configured_percentage"
      ? priceHistoryClaim.savingsPct
      : 0;
  const originalPrice =
    priceHistoryClaim?.displayRule === "absolute_amount"
      ? priceHistoryClaim.pMin30Eur
      : priceHistoryClaim?.displayRule === "configured_percentage"
        ? priceHistoryClaim.referencePriceEur
      : null;
  const savingsEur =
    priceHistoryClaim?.displayRule === "absolute_amount"
      ? priceHistoryClaim.savingsEur
      : null;
  // Eén gedeelde beslissing over de hele prijsclaim (Dennis 28-09-2026,
  // "B + akkoord"; zelfde helper als de dealpagina en DealDetailClient):
  // van-prijs in hele eenheden omlaag, het DE-bespaarbedrag uit de GETOONDE
  // getallen (nooit hoger dan de backend). Valt de van-prijs niet meer boven
  // de getoonde nu-prijs, dan vervalt de HELE claim op de kaart: van-cel,
  // bespaar-/korting-cel en de badge op de foto.
  // Uitzondering (Dennis 28-09-2026, "de korting mag blijven staan"): zou
  // de afgeronde van-prijs niet boven de nu-prijs liggen maar de exacte
  // referentie wel, dan exact op centen (vanPrecision "cents"); pas als
  // ook die niet erboven ligt vervalt de claim. Zie price-math.js.
  const claimDisplay = resolvePriceClaimDisplay({
    displayRule: priceHistoryClaim?.displayRule,
    originalAmount: toCurrency(originalPrice),
    currentAmount: toCurrency(actualPrice),
    savingsAmount: toCurrency(savingsEur),
  });
  const savingsAmountText =
    claimDisplay.showClaim && claimDisplay.savingsAmount > 0
      ? formatPriceText(claimDisplay.savingsAmount, { currency, locale })
      : "";
  const priceHistoryBadgeText =
    priceHistoryClaim?.displayRule === "absolute_amount" && savingsAmountText
      ? t("deal.price.savings_amount_text", "Bespaar {amount}", {
          amount: savingsAmountText,
        })
      : (priceHistoryClaim?.displayRule === "badge" || priceHistoryClaim?.displayRule === "configured_percentage") &&
          discount > 0 &&
          claimDisplay.showClaim
        ? `-${discount}%`
        : null;
  // Categoriesticker (kerstmarkten): alleen de tekst, geen bedrag of korting.
  const categoryStickerDef = categoryStickerFor(deal);
  const categorySticker = categoryStickerDef ? t(categoryStickerDef[0], categoryStickerDef[1]) : null;
  const showOriginalPrice =
    !isVoucher &&
    (priceHistoryClaim?.displayRule === "absolute_amount" || priceHistoryClaim?.displayRule === "configured_percentage") &&
    originalPrice > 0 &&
    claimDisplay.showClaim &&
    claimDisplay.vanAmount !== null;
  const showPriceClaimCell =
    !isVoucher &&
    claimDisplay.showClaim &&
    (priceHistoryClaim?.displayRule === "badge" ||
      priceHistoryClaim?.displayRule === "configured_percentage" ||
      (priceHistoryClaim?.displayRule === "absolute_amount" && claimDisplay.savingsAmount > 0));
  const priceGridClass = showPriceClaimCell
    ? showOriginalPrice
      ? "grid-cols-3"
      : "grid-cols-2"
    : "grid-cols-1";
  const rawNights = deal.length_of_stay;
  const nights = Array.isArray(rawNights) ? rawNights : (typeof rawNights === "string" ? (() => { try { return JSON.parse(rawNights); } catch { return []; } })() : []);
  // INTL bug-batch 2026-06-10 item 5: "2,3" read as a decimal number on
  // .de/.fr ("2,3 Tage" = two-point-three days). Render the stay-length
  // options as a range with a hyphen: "2-3".
  const nightsLabel = nights.length > 0
    ? (Math.min(...nights) === Math.max(...nights)
        ? String(Math.min(...nights))
        : `${Math.min(...nights)}-${Math.max(...nights)}`)
    : "";

  const hasActivity = dealHasActivity(deal);

  // Build href with slug URL + filter params for booking flow.
  //
  // Codex round-9 BUG 10 (2026-05-09): voucher cards are
  // party-config-agnostic — a gift voucher has no fixed pax cap and
  // never threads adults/children into a booking, so the URL must NOT
  // carry homepage-search filterParams. Round-9 verify-prompt asserts
  // strict shape `/vouchers/<slug>?q=<id>` (no extra params).
  // Pre-fix the filterParams forwarder appended `&adults=…&children=…`
  // to every card href including vouchers, which violated the
  // strict-shape contract AND added noise the resolver doesn't use.
  const baseHref = isVoucher
    ? voucherUrl(deal, locale)
    : dealUrl(deal, categoryName, locale, { farTravelEnabled: farTravelBookingEnabled === true });

  let href;
  if (typeof hrefOverride === "string" && hrefOverride) {
    href = hrefOverride;
  } else if (isVoucher) {
    // Strict voucher contract: no party params.
    href = baseHref;
  } else {
    const extraParams = new URLSearchParams();
    if (filterParams?.adults && filterParams.adults !== "2") extraParams.set("adults", filterParams.adults);
    if (filterParams?.children && filterParams.children !== "0") extraParams.set("children", filterParams.children);
    if (filterParams?.nights) extraParams.set("nights", filterParams.nights);
    if (filterParams?.date) extraParams.set("date", filterParams.date);
    const extra = extraParams.toString();
    const separator = baseHref.includes("?") ? "&" : "?";
    href = extra ? `${baseHref}${separator}${extra}` : baseHref;
  }
  const trackingId = deal?.deals_id ?? deal?.id;
  // Het register wint van de vaste lijst van #1800 (beeldExtra uit DealsCarousel).
  // TODO: de vaste lijst (kaartfilms.js) mag weg zodra DEAL_VIDEO_PUBLIEK aan staat.
  const registerFilm = dealFilmsToegestaan && !bewegingUit ? dealFilmUit(dealFilms, trackingId) : null;
  const kaartBeeldExtra = registerFilm?.kaart ? (
    <KaartFilm
      key={registerFilm.kaart}
      mp4={registerFilm.kaart}
      poster={registerFilm.poster || undefined}
      meet={{ dealsId: trackingId, plek: "kaart", groep: dealFilms.groep }}
    />
  ) : (
    beeldExtra
  );
  const raster = opmaak === "raster";

  function handleClick() {
    fireClickBeacon(trackingId, source, position);
  }

  // Voucher price is already for 2 persons, deal price is per person.
  // Kartik #69 (2026-04-18): card used to compute `actualPrice * 2`
  // while the detail page used `actualPrice * 2 + 19` — both labelled
  // "incl. reserveringskosten €19" but only the detail page actually
  // added the fee, so the two prices diverged. Single helper now.
  // LP26: dezelfde categorieherkenning als de boekflow, ook zonder Luxe-link.
  // Laat de bestaande totaalregel voor alle andere kaarten ongewijzigd.
  const isLuxeCard = isLuxeDeal(deal);
  // Kerstmarkten zonder reserveringskosten (Dennis 24-10-2026): `reservation_fee_eur`
  // van de deal. Bij 0 geen reserveringskosten in totaal of tekst; zonder
  // veld blijft het RESERVATION_FEE_EUR, exact het beeld van daarvoor.
  // Fixronde 5: een lijst heeft geen prijsopgave, dus hier telt alleen
  // `deal.reservation_fee_eur === 0` (isAllInclusiveReservationFee). De
  // backend (site-backend#4588) zet dat veld alleen bij een gevulde
  // schakelaar, uit dezelfde lezing als de prijsopgave; een afwezig veld
  // geeft het bijschrift van main.
  const cardReservationFeeEur = resolveReservationFeeEur(deal, RESERVATION_FEE_EUR);
  const cardIsAllInclusive = isAllInclusiveReservationFee(deal);
  const totalPrice =
    isVoucher || isLuxeCard
      ? null
      : cardIsAllInclusive
        ? computePackageTotal(actualPrice, { includeFee: false })
        : computePackageTotal(actualPrice, { feeEur: cardReservationFeeEur });

  return (
    <article
      className={
        raster
          ? "group/card flex flex-col h-full"
          : "group/card overflow-hidden flex flex-col h-full transition-shadow hover:shadow-lg"
      }
      style={raster ? undefined : { border: "1px solid #E7E7F4", borderRadius: "8px" }}
    >
      <Link href={href} className="block" onClick={handleClick}>
        {/* Image — Angular: height 320px on homepage carousel, width 100%, object-fit cover */}
        <div className={raster ? "relative overflow-hidden bg-gray-100 aspect-[4/3] rounded-xl" : "relative overflow-hidden bg-gray-100 aspect-[16/9]"}>
          <Image
            src={imgSrc}
            alt={displayTitle}
            fill
            sizes={imageSizes}
            quality={CARD_IMAGE_QUALITY}
            // Next 16 needs preload to discover the first image in the head;
            // eager/fetchPriority alone only add hints to the img element.
            preload={position === 0}
            loading={position === 0 ? undefined : (imageLoading ?? "lazy")}
            placeholder={`data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="16" height="9"><defs><linearGradient id="g" x2="1" y2="1"><stop stop-color="#e8eee9"/><stop offset="1" stop-color="#f2eee5"/></linearGradient></defs><path fill="url(#g)" d="M0 0h16v9H0z"/></svg>')}`}
            fetchPriority={position === 0 ? "high" : undefined}
            className="object-cover group-hover/card:scale-105 transition-transform duration-300"
          />
          {kaartBeeldExtra}
          {/* Price-history claim badge — rectangular, matches original: #FB2400, 6px radius */}
          {/* Voorrang: een echte kortingsclaim wint; de categoriesticker staat
              alleen op de plek als die ontbreekt (nooit twee tegelijk). */}
          {(priceHistoryBadgeText || categorySticker) && (
            <span
              data-testid={priceHistoryBadgeText ? undefined : "deal-category-sticker"}
              className="absolute top-3 left-3 text-white"
              style={{
                backgroundColor: "#d42100",
                borderRadius: "6px",
                padding: "0 10px",
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "28px",
              }}
            >
              {priceHistoryBadgeText || categorySticker}
            </span>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className={raster ? "pt-3.5 flex flex-col flex-1" : "p-3 sm:p-4 flex flex-col flex-1"}>
        <Link href={href} className="block flex-1" onClick={handleClick}>
          {/* Icons row + voucher badge — Angular shows badge to the right of icons */}
          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
            <div className="flex items-center gap-1.5">
              {/* Bed/hotel icon */}
              <img src="/images/icons/bed-icon.svg" alt={deTekst(locale, "sx.dealcard.alt_hotel", "Hotel inbegrepen")} width={22} height={16} />
              {hasActivity && (
                <>
                  <img src="/images/icons/plus-icon.svg" alt="" width={10} height={11} />
                  <img src="/images/icons/ticket-icon.svg" alt={deTekst(locale, "sx.dealcard.alt_activiteit", "Activiteit inbegrepen")} width={20} height={17} />
                </>
              )}
            </div>
            {/* Voucher badge — blue, matches original: #0A8DE9, 4px radius, positioned right */}
            <div className="flex items-center gap-2">
              {showRecordedBadge && <RecordedBadge />}
              {isVoucher && (
                <div
                  className="flex items-center gap-1.5"
                  style={{
                    backgroundColor: "#0070b8",
                    borderRadius: "4px",
                    padding: "4px 8px",
                  }}
                >
                  <img src="/images/icons/ticket-white.svg" alt={t("listing.voucher_badge", "Voucher")} width={14} height={14} />
                  <span style={{ color: "white", fontSize: "11px", fontWeight: 600 }}>{t("listing.voucher_badge", "Voucher")}</span>
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <h3
            className={
              raster
                ? "font-bold text-gray-900 text-[18px] leading-[1.3] line-clamp-2 mb-auto min-h-[2.6em] group-hover/card:text-blue-600 transition-colors"
                : "font-bold text-gray-900 text-sm sm:text-[15px] leading-snug line-clamp-2 mb-auto min-h-[2.25rem] sm:min-h-[2.5rem] group-hover/card:text-blue-600 transition-colors"
            }
          >
            {displayTitle}
          </h3>

          {/* Dennis 28-09-2026: "Keuze uit N hotels" weghalen (nooit
              gevraagd, kwam uit audit #56/fe#1195). Niet terugzetten
              zonder zijn opdracht. */}
        </Link>

        {/* Price section - hidden on homepage deal cards (FIX 4) */}
        {showPricing && !isHomepage && (
          <div className={raster ? "mt-auto pt-3" : "mt-2 sm:mt-3"}>
            {/* Price row.
             * - With DE amount claim: 3-column grid (van | nu vanaf | bespaar).
             * - With NL badge claim: 2-column grid (nu vanaf | korting).
             * - Without claim (or voucher with no claim): only the single
             *   "nu vanaf" / voucher-price block renders; collapse to grid-cols-1
             *   so the lone child fills the row and `text-center` centers it
             *   horizontally instead of being stuck in the left half of a
             *   2-column grid (Kartik #98, 2026-04-27). */}
            <div className={`grid ${priceGridClass} ${raster ? "text-left gap-x-4" : "text-center"}`}>
              {isVoucher ? (
                <div>
                  <span className={raster ? "text-[15px] text-gray-500 block leading-tight" : "text-[14px] text-gray-500 block leading-tight"}>{t("deal_card.price_for")}</span>
                  <span className={raster ? "text-[28px] font-bold text-gray-900" : "text-[18px] font-semibold text-gray-900"}>
                    {formatPrice(toCurrency(actualPrice), { currency, includeSymbol: true, locale })}
                  </span>
                </div>
              ) : (
                <>
                  {/* Van (original price) — left */}
                  {showOriginalPrice && (
                    <div>
                      <span className={raster ? "text-[15px] text-gray-500 block leading-tight" : "text-[14px] text-gray-500 block leading-tight"}>{t("deal_card.price_from")}</span>
                      <span className="text-[18px] font-semibold text-gray-500 line-through">
                        {formatStrikethroughPrice(claimDisplay.vanAmount, {
                          currency,
                          includeSymbol: true,
                          locale,
                          precision: claimDisplay.vanPrecision,
                        })}
                      </span>
                    </div>
                  )}
                  {/* Nu vanaf — center */}
                  <div>
                    <span className={raster ? "text-[15px] text-gray-500 block leading-tight" : "text-[14px] text-gray-500 block leading-tight"}>{t("deal_card.price_now_from")}</span>
                    <span className={raster ? "text-[28px] font-bold text-gray-900" : "text-[18px] font-semibold text-gray-900"}>
                      {formatPrice(toCurrency(actualPrice), { currency, includeSymbol: true, locale })}
                    </span>
                  </div>
                </>
              )}

              {/* Price-history claim — right */}
              {showPriceClaimCell && (
                <div>
                  <span className={raster ? "text-[15px] text-gray-500 block leading-tight" : "text-[14px] text-gray-500 block leading-tight"}>
                    {priceHistoryClaim.displayRule === "absolute_amount"
                      ? t("deal.price.savings_amount_label", "Bespaar")
                      : t("deal_card.price_discount")}
                  </span>
                  <span className="text-[18px] font-semibold text-gray-900">
                    {priceHistoryClaim.displayRule === "absolute_amount"
                      ? formatPrice(claimDisplay.savingsAmount, {
                          currency,
                          includeSymbol: true,
                          locale,
                        })
                      : `-${discount}%`}
                  </span>
                </div>
              )}
            </div>

            <p className={raster ? "text-[15px] text-gray-500 mt-0.5 text-left" : "text-[14px] text-gray-500 mt-0.5 text-center"}>
              {isVoucher ? t("deal_card.per_2_persons") : t("deal_card.per_person")}
            </p>

            {/* Total price line with tooltip */}
            {totalPrice > 0 && (
              <div className="relative mt-2 sm:mt-3">
                <p className={raster ? "text-[13px] text-gray-500 flex items-start gap-1 leading-relaxed" : "text-[10px] sm:text-[11px] text-gray-500 flex items-start gap-1 leading-relaxed"}>
                  {cardIsAllInclusive ? (
                    <span data-testid="deal-card-total-all-in">
                      {t("deal.total_hint_all_in_tpl").replace("{total}", formatPriceText(toCurrency(totalPrice), { currency, locale }))}
                    </span>
                  ) : (
                    <span>
                      {t("deal_card.totaal_prefix")}{formatPriceText(toCurrency(totalPrice), { currency, locale })}{t("deal_card.totaal_middle_2pers")}{nightsLabel || "2-4"}{t("deal_card.totaal_middle_days")}{formatPriceText(toCurrency(cardReservationFeeEur), { currency, locale })}{t("deal_card.totaal_suffix")}
                    </span>
                  )}
                  <span className="group/info relative inline-flex flex-shrink-0 cursor-help">
                    <svg className="w-3.5 h-3.5 mt-0.5 text-gray-400 hover:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    <span className="absolute bottom-full right-0 mb-1 w-48 p-2 bg-gray-900 text-white text-[10px] rounded shadow-lg opacity-0 pointer-events-none group-hover/info:opacity-100 group-hover/info:pointer-events-auto transition-opacity z-50">
                      {t("deal_card.price_disclaimer")}
                    </span>
                  </span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
